/**
 * @file Renders one derived value's source as the sentence spec §3 note 4 puts
 * in the admin list and spec §8 puts in the filler's popover — one spelling, so
 * the two can never disagree. Pure: `labels` supplies every lookup, and an id it
 * cannot place renders raw, which is what marks the row Invalid. Invariants:
 * narrow the algebraic arm with `'operands' in entry` and the row projection
 * with `'columns' in entry` before touching `source.kind`; `›` (U+203A)
 * separates table from column; rounding attaches to the value phrase with a
 * comma, and the secondary facts — input count, live reads, row effects —
 * attach with ` · `, in that order and never reordered. A projection's sentence
 * names its partition key and COUNTS its declared columns rather than listing
 * them: the per-column phrase is `describeFormDeriveRowValue`'s.
 */
import type { ArColumnAggregation, FormDeriveEntry, FormDeriveReferenceProp } from '#types';
import ArColumnAggregationOptions from '@/components/settings/ArTableEditorTab/ArColumnAggregationOptions';
import { formDeriveCopy } from './formDeriveCopy';
import type { FormDeriveLabelResolvers } from './formDeriveLabelResolvers';

/**
 * Spec §4: read off the same options the drawer's Aggregation select renders, so the
 * select and this sentence cannot word one fold two ways.
 */
function aggregationLabel(aggregation: ArColumnAggregation): string {
  return (
    ArColumnAggregationOptions.find((option) => option.value === aggregation)?.label ?? aggregation
  );
}

/** The four operators the expression admits, in the glyphs §5's token pills use. */
function displayExpression(expression: string): string {
  return expression.replaceAll('-', '−').replaceAll('*', '×').replaceAll('/', '÷');
}

function propLabel(prop: FormDeriveReferenceProp, labels: FormDeriveLabelResolvers): string {
  if (prop.kind === 'name') return formDeriveCopy.referencePropName;
  if (prop.kind === 'field') return labels.fieldLabel(prop.fieldId) ?? prop.fieldId;
  return labels.referenceLabel(prop.referenceKey) ?? prop.referenceKey;
}

function describeFormDeriveSource(entry: FormDeriveEntry, labels: FormDeriveLabelResolvers): string {
  const rounding = entry.roundToCents ? ', rounded to cents' : '';

  if ('operands' in entry) {
    const count = entry.operands.length;
    const inputs = count === 1 ? '1 input' : `${count} inputs`;
    return `${displayExpression(entry.expression)}${rounding} · ${inputs}`;
  }

  if ('columns' in entry) {
    const { tableId, typeId } = entry.source;
    const table = labels.tableLabel(tableId) ?? tableId;
    const column = labels.columnLabel(tableId, typeId) ?? typeId;
    const count = entry.columns.length;
    const columns = count === 1 ? '1 column' : `${count} columns`;
    const readsLive = entry.columns.some(({ value }) => value.kind === 'rowReferenceProp')
      ? ' · read live'
      : '';
    return `Distinct ${table} › ${column} · ${columns}${readsLive} · creates and deletes rows`;
  }

  const { source } = entry;
  if (source.kind === 'field') return labels.fieldLabel(source.fieldId) ?? source.fieldId;
  if (source.kind === 'referenceProp') {
    const reference = labels.referenceLabel(source.referenceKey) ?? source.referenceKey;
    return `${reference} › ${propLabel(source.prop, labels)} · read live`;
  }

  const table = labels.tableLabel(source.tableId) ?? source.tableId;
  const typeId =
    source.aggregationFieldPath === undefined
      ? source.typeId
      : `${source.typeId}.${source.aggregationFieldPath}`;
  const column = labels.columnLabel(source.tableId, typeId) ?? typeId;
  return `${aggregationLabel(source.aggregation)} of ${table} › ${column}${rounding}`;
}

export { describeFormDeriveSource };
